import { sparkApi } from '@/api/spark-client';
import { type GetEngagementDetailsToolCall } from '@/features/prince/messages/formatter/toolCalls/tools/chorus/GetEngagementDetailsToolCall';
import { type GetTranscriptToolCall } from '@/features/prince/messages/formatter/toolCalls/tools/chorus/GetTranscriptToolCall';
import { type SearchEngagementsToolCall } from '@/features/prince/messages/formatter/toolCalls/tools/chorus/SearchEngagementsToolCall';
import { type SummarizeTranscriptToolCall } from '@/features/prince/messages/formatter/toolCalls/tools/chorus/SummarizeTranscriptToolCall';
import { type CreateRecordToolCall } from '@/features/prince/messages/formatter/toolCalls/tools/salesforce/CreateRecordToolCall';
import { type DeleteRecordToolCall } from '@/features/prince/messages/formatter/toolCalls/tools/salesforce/DeleteRecordToolCall';
import type { FieldDiscoverToolCall } from '@/features/prince/messages/formatter/toolCalls/tools/salesforce/FieldDiscoverToolCall';
import { type GetListViewsToolCall } from '@/features/prince/messages/formatter/toolCalls/tools/salesforce/GetListViews';
import { type GetSObjectToolCall } from '@/features/prince/messages/formatter/toolCalls/tools/salesforce/GetSObject';
import { type ObjectDiscoverToolCall } from '@/features/prince/messages/formatter/toolCalls/tools/salesforce/ObjectDiscoverToolCall';
import { type QueryAllToolCall } from '@/features/prince/messages/formatter/toolCalls/tools/salesforce/QueryAllToolCall';
import { type SoqlQueryToolCall } from '@/features/prince/messages/formatter/toolCalls/tools/salesforce/SoqlToolCall';
import { type SoslSearchToolCall } from '@/features/prince/messages/formatter/toolCalls/tools/salesforce/SoslToolCall';
import { type UpdateRecordToolCall } from '@/features/prince/messages/formatter/toolCalls/tools/salesforce/UpdateRecordToolCall';
import { type Message, MessageType } from '@/features/prince/messages/types';
import { Agent, Model } from '@/features/prince/models';
import { MessagePartType, type ToolCallSummary } from '@/features/types/tools';
import i18n from '@/i18n/config';
import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryFunctionContext,
} from '@tanstack/react-query';

const MESSAGE_PAGE_SIZE = 20;

interface BackendConversationMessage {
  id?: string | null;
  agent_run_id?: string;
  content: string;
  sender_user_id: string;
  message_type: MessageType;
  user_rating?: { score: number };
  created_at: string;
}

interface BackendConversation {
  id: string;
  title: string;
  conversation_type: string;
  owner_user_id: string;
  agent_id: string | null;
  created_at: string;
  last_updated: string;
  is_favorite: boolean;
  is_deleted: boolean;
}

interface ChatHistoryApiResponse<TConversation = BackendConversation> {
  conversation: TConversation;
  conversation_messages: BackendConversationMessage[];
  has_more?: boolean;
  oldest_message_id?: string;
}

interface ChatHistoryResult<TConversation = BackendConversation> {
  conversation: TConversation;
  messages: Message[];
}

const createChatHistoryResult = <TConversation>(
  data: ChatHistoryApiResponse<TConversation>
): ChatHistoryResult<TConversation> => ({
  conversation: data.conversation,
  messages: data.conversation_messages.map(normalizeConversationMessage),
});

const normalizeConversationMessage = (message: BackendConversationMessage): Message => {
  const messageId = message.id || crypto.randomUUID();
  const isAiMessage = message.message_type === MessageType.AI_AGENT;
  const content = isAiMessage ? parseAiMessageContent(message.content) : message.content;

  return {
    id: messageId,
    agentRunId: message.agent_run_id,
    type: message.message_type,
    content,
    timestamp: message.created_at,
    attachments: [],
    isStreaming: false,
    ...(message.user_rating && { userRating: message.user_rating }),
  } as Message;
};

// Backend API Types (only for internal use)

export interface StreamInput {
  message: string;
  model?: Model;
  conversation_id: string;
}

export enum StreamEventType {
  Token = 'token',
  Message = 'message',
  Error = 'error',
  Done = 'done',
}

export enum StreamMessageType {
  Ai = 'ai',
  Tool = 'tool',
}

export type StreamMessage =
  | {
      type: StreamMessageType.Ai;
      content: string;
      tool_calls: Array<
        | SoqlQueryToolCall
        | ObjectDiscoverToolCall
        | FieldDiscoverToolCall
        | SoslSearchToolCall
        | GetListViewsToolCall
        | GetSObjectToolCall
        | CreateRecordToolCall
        | UpdateRecordToolCall
        | DeleteRecordToolCall
        | QueryAllToolCall
        | GetEngagementDetailsToolCall
        | GetTranscriptToolCall
        | SearchEngagementsToolCall
        | SummarizeTranscriptToolCall
      >;
      tool_call_id: string | null;
      run_id: string;
      response_metadata: {
        finish_reason: 'tool_calls';
        model_name: string;
        system_fingerprint: string;
        service_tier: string;
        auto_continuation?: {
          segments?: unknown[];
          finish_reasons?: string[];
          continuation_count?: number;
          limit_reached?: boolean;
          continuation_attempts?: number;
          max_auto_continuations?: number;
        };
      };
      custom_data?: unknown;
    }
  | {
      type: StreamMessageType.Tool;
      content: string;
      tool_calls: [];
      tool_call_id: string;
      run_id: string;
      response_metadata: unknown;
      custom_data: unknown;
    };

export type StreamEvent =
  | {
      type: StreamEventType.Token;
      content: string;
    }
  | {
      type: StreamEventType.Message;
      content: StreamMessage;
    }
  | {
      type: StreamEventType.Error;
      error: string;
      isAbortError?: boolean;
    }
  | {
      type: StreamEventType.Done;
    };

/**
 * Query options for fetching conversations
 */
export const conversationsQueryOptions = () =>
  queryOptions({
    queryKey: ['conversations'],
    queryFn: async ({ signal }) => {
      try {
        const response = await sparkApi.get('/api/v1/agents/conversations', { signal });

        // Map backend conversation data to frontend ChatHistoryItem[]
        return response.data.map(
          (conv: { id: string; title: string; created_at: string; is_favorite: boolean }) => ({
            id: conv.id,
            title: conv.title, // Use the title from the backend
            isActive: false, // Will be set by the component based on current conversation
            isFavorite: conv.is_favorite,
          })
        );
      } catch (error: unknown) {
        console.error('Failed to fetch conversations from new endpoint:', error);
        throw new Error('Failed to load conversations');
      }
    },
    staleTime: 1000 * 60 * 1, // 1 minute
  });

export const chatHistoryQueryOptions = (conversationId: string, enabled: boolean = true) =>
  queryOptions({
    queryKey: ['conversations', conversationId, 'messages'],
    enabled: enabled && !!conversationId,
    queryFn: async ({ signal }) => {
      try {
        const response = await sparkApi.get<ChatHistoryApiResponse>(
          `/api/v1/agents/conversations/${conversationId}/messages`,
          {
            signal,
          }
        );

        return createChatHistoryResult(response.data);
      } catch (error: unknown) {
        console.error('Failed to fetch chat history from new endpoint:', error);
        throw new Error('Failed to load chat history');
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

export const chatHistoryInfiniteQueryOptions = (conversationId: string, enabled: boolean = true) =>
  infiniteQueryOptions({
    queryKey: ['conversations', conversationId, 'messages', 'infinite'],
    enabled: enabled && !!conversationId,
    queryFn: async ({ signal, pageParam }: QueryFunctionContext<string[], string | undefined>) => {
      try {
        const params = new URLSearchParams({
          limit: MESSAGE_PAGE_SIZE.toString(),
          ...(pageParam ? { before_id: pageParam } : {}),
        });

        const response = await sparkApi.get<ChatHistoryApiResponse>(
          `/api/v1/agents/conversations/${conversationId}/messages?${params}`,
          { signal }
        );

        const chatHistory = createChatHistoryResult(response.data);

        return {
          ...chatHistory,
          hasMore: Boolean(response.data.has_more),
          oldestMessageId: response.data.oldest_message_id,
        };
      } catch (error: unknown) {
        console.error('Failed to fetch chat history from new endpoint:', error);
        throw new Error('Failed to load chat history');
      }
    },
    initialPageParam: undefined,
    getNextPageParam: lastPage => {
      return lastPage.hasMore ? lastPage.oldestMessageId : undefined;
    },
    staleTime: 1000 * 60 * 5,
  });

/**
 * Mutation for deleting a conversation
 */
export const useDeleteConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId: string) => {
      const response = await sparkApi.delete(`/api/v1/agents/conversations/${conversationId}`);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate conversations query to refresh the list
      queryClient.invalidateQueries({
        queryKey: ['conversations'],
      });
    },
    onError: error => {
      console.error('Failed to delete conversation:', error);
      throw new Error('Failed to delete conversation');
    },
    retry: 1,
  });
};

/**
 * Mutation for toggling favorite status
 */
export const useToggleFavorite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId: string) => {
      const response = await sparkApi.post(
        `/api/v1/agents/conversations/${conversationId}/favorite`
      );
      return response.data.is_favorite;
    },
    onMutate: async conversationId => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['conversations'] });

      // Snapshot the previous value
      const previousConversations = queryClient.getQueryData(['conversations']);

      // Optimistically update to the new value
      queryClient.setQueryData(
        ['conversations'],
        (
          old:
            | Array<{ id: string; title: string; isActive: boolean; isFavorite: boolean }>
            | undefined
        ) => {
          if (!old) return old;
          return old.map(conv =>
            conv.id === conversationId ? { ...conv, isFavorite: !conv.isFavorite } : conv
          );
        }
      );

      // Return a context object with the snapshotted value
      return { previousConversations };
    },
    onError: (err, _conversationId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousConversations) {
        queryClient.setQueryData(['conversations'], context.previousConversations);
      }
      console.error('Failed to toggle favorite:', err);
      throw new Error('Failed to toggle favorite');
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    retry: 1,
  });
};

export const useMessageRatingMutation = () => {
  return useMutation({
    mutationFn: async (rating: { runId: string; score: number; comment?: string }) => {
      await sparkApi.post('/api/v1/agents/messages/rating', rating);
    },
  });
};

export enum StreamRequestType {
  NewUserMessage = 'new-user-message',
  RegenerateAgentResponse = 'regenerate-agent-response',
}

export type StreamRequest =
  | {
      type: StreamRequestType.NewUserMessage;
      message: string;
      run_id: string;
    }
  | {
      type: StreamRequestType.RegenerateAgentResponse;
      run_id: string;
      run_id_to_regenerate: string;
    };

/**
 * Stream chat message responses using Server-Sent Events
 * Returns an async generator that yields streaming events
 */
export async function* streamChatMessage(
  request: StreamRequest,
  model: Model,
  conversationId: string,
  agent: Agent,
  abortController?: AbortController
): AsyncGenerator<StreamEvent, void, unknown> {
  const payload = {
    conversation_id: conversationId,
    run_id: request.run_id,
    model: model,
    stream_tokens: true,
    agent_config: {},
    ...(request.type === StreamRequestType.NewUserMessage
      ? { message: request.message }
      : { run_id_to_regenerate: request.run_id_to_regenerate }),
  };

  const url =
    request.type === StreamRequestType.NewUserMessage
      ? `${sparkApi.defaults.baseURL}/api/v1/agents/${agent}/stream`
      : `${sparkApi.defaults.baseURL}/api/v1/agents/${agent}/regenerate`;

  // Use fetch for SSE since Axios doesn't handle streaming well
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    // Send session cookie for cross-origin request
    credentials: 'include',
    body: JSON.stringify(payload),
    signal: abortController?.signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  if (!response.body) {
    throw new Error('Response body is null');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      // Decode the chunk and add to buffer
      buffer += decoder.decode(value, { stream: true });

      // Process complete lines
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.trim() === '') continue;

        // Handle SSE format: "data: {json}" or special markers
        if (line.startsWith('data: ')) {
          const data = line.slice('data: '.length);

          if (data.trim() === '[DONE]') {
            yield { type: StreamEventType.Done };
            return;
          }

          try {
            const parsed = JSON.parse(data);

            if (parsed.type === 'token' && parsed.content) {
              yield {
                type: StreamEventType.Token,
                content: parsed.content,
              };
            } else if (parsed.type === 'message' && parsed.content) {
              yield {
                type: StreamEventType.Message,
                content: parsed.content,
              };
            } else if (parsed.error || parsed.type === 'error') {
              yield {
                type: StreamEventType.Error,
                error: parsed.error || parsed.content,
              };
            }
          } catch (parseError) {
            console.error('Failed to parse SSE data:', parseError, 'Data:', data);
            // Continue processing other events
          }
        }
      }
    }
  } catch (error) {
    yield {
      type: StreamEventType.Error,
      error: error instanceof Error ? error.message : 'Unknown streaming error',
      isAbortError: error instanceof Error && error.name === 'AbortError',
    };
  } finally {
    reader.releaseLock();
  }
}

/**
 * Upload and parse a document via the unified document parse endpoint.
 * Automatically sets llmPDF=true for PDFs, false for other supported types.
 * Returns parsed JSON (or raw text if response is not JSON).
 */
export async function parseDocument<T extends object = Record<string, unknown>>(
  file: File
): Promise<T | string> {
  const supportedExtensions = new Set([
    'pdf',
    'docx',
    'xlsx',
    'pptx',
    'html',
    'xhtml',
    'md',
    'csv',
    'png',
    'jpeg',
    'jpg',
    'tiff',
    'bmp',
    'webp',
    'wav',
    'mp3',
    'txt',
    'xml',
  ]);

  const ext = (file.name.split('.').pop() || '').toLowerCase();
  if (!supportedExtensions.has(ext)) {
    throw new Error(`Unsupported file type: .${ext}`);
  }

  const isPdf = ext === 'pdf';

  const form = new FormData();
  form.append('file', file);
  form.append('llmPDF', String(isPdf));

  const resp = await fetch(`${sparkApi.defaults.baseURL}/api/v1/document/parse`, {
    method: 'POST',
    body: form,
    headers: {
      Accept: 'application/json',
    },
    credentials: 'include',
  });

  const text = await resp.text();
  try {
    return JSON.parse(text) as T;
  } catch {
    return text;
  }
}

export interface PrinceUploadResponse {
  url: string;
  expiresAt: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  tempKey: string;
}

/**
 * Upload a file to temporary storage with 30-minute expiring public URL.
 * Designed for AI processing and MCP tool integration.
 * Requires bearer token authentication.
 */
export async function uploadPrinceFile(
  file: File,
  conversationId: string
): Promise<PrinceUploadResponse> {
  const supportedExtensions = new Set([
    'pdf',
    'docx',
    'xlsx',
    'pptx',
    'html',
    'xhtml',
    'md',
    'csv',
    'png',
    'jpeg',
    'jpg',
    'tiff',
    'bmp',
    'webp',
    'wav',
    'mp3',
    'txt',
    'xml',
  ]);

  const ext = (file.name.split('.').pop() || '').toLowerCase();
  if (!supportedExtensions.has(ext)) {
    throw new Error(`Unsupported file type: .${ext}`);
  }

  // Check file size (25MB limit)
  const maxSize = 25 * 1024 * 1024; // 25MB
  if (file.size > maxSize) {
    throw new Error(
      i18n.t('queries.fileUploadMaxSizeError', {
        fileSize: file.size,
        maxSize: maxSize,
        formatParams: {
          fileSize: { style: 'unit', unit: 'byte', unitDisplay: 'long', minimumFractionDigits: 0 },
          maxSize: { style: 'unit', unit: 'byte', unitDisplay: 'long', minimumFractionDigits: 0 },
        },
      })
    );
  }

  const form = new FormData();
  form.append('file', file);
  form.append('conversation_id', conversationId);

  const resp = await fetch(`${sparkApi.defaults.baseURL}/api/v1/storage/upload-prince-file`, {
    method: 'POST',
    body: form,
    credentials: 'include', // Include bearer token via session
  });

  if (!resp.ok) {
    const errorText = await resp.text();
    throw new Error(`Upload failed: ${errorText}`);
  }

  const result = await resp.json();
  return result as PrinceUploadResponse;
}

// Helper types for message parsing
interface ToolCallItem {
  type: MessagePartType.ToolCall;
  id: string;
  name: string;
  args: object;
  results?: string;
}

interface ToolResponseItem {
  type: MessagePartType.ToolResponse;
  tool_call_id: string;
  content: string;
}

type MessageContentItem = string | ToolCallItem | ToolResponseItem;

// Helper functions for message content parsing
function parseAiMessageContent(content: string): Array<string | ToolCallSummary> {
  try {
    const parsedContent = JSON.parse(content);

    if (Array.isArray(parsedContent)) {
      return processMessageArray(parsedContent);
    }

    // Single content item - wrap in array
    return [parsedContent];
  } catch (error) {
    console.warn('Failed to parse AI message content as JSON, treating as plain text:', error);
    return [content];
  }
}

function processMessageArray(items: MessageContentItem[]): Array<string | ToolCallSummary> {
  const toolResponseMap = buildToolResponseMap(items);

  return items
    .map(item => processMessageItem(item, toolResponseMap))
    .filter((item): item is string | ToolCallSummary => item !== null);
}

function buildToolResponseMap(items: MessageContentItem[]): Map<string, string> {
  const toolResponseMap = new Map<string, string>();

  items.forEach(item => {
    if (isToolResponseItem(item)) {
      toolResponseMap.set(item.tool_call_id, item.content);
    }
  });

  return toolResponseMap;
}

function processMessageItem(
  item: MessageContentItem,
  toolResponseMap: Map<string, string>
): string | ToolCallSummary | null {
  if (typeof item === 'string') {
    return item;
  }

  if (isToolCallItem(item)) {
    return createToolCallSummary(item, toolResponseMap);
  }

  // Skip tool_response items as they're handled via the map
  if (isToolResponseItem(item)) {
    return null;
  }

  // Unknown item type - return as string
  return String(item);
}

function createToolCallSummary(
  toolCall: ToolCallItem,
  toolResponseMap: Map<string, string>
): ToolCallSummary {
  const results = toolCall.results || toolResponseMap.get(toolCall.id);
  const hasResults = Boolean(results && results.trim());

  return {
    type: MessagePartType.ToolCall,
    name: toolCall.name,
    args: toolCall.args,
    id: toolCall.id,
    results: hasResults ? results : undefined,
    isRunning: !hasResults,
  } as ToolCallSummary;
}

// Type guards for better type safety
function isToolCallItem(item: MessageContentItem): item is ToolCallItem {
  return (
    typeof item === 'object' &&
    item !== null &&
    'type' in item &&
    item.type === MessagePartType.ToolCall &&
    'id' in item &&
    'name' in item &&
    'args' in item
  );
}

function isToolResponseItem(item: MessageContentItem): item is ToolResponseItem {
  return (
    typeof item === 'object' &&
    item !== null &&
    'type' in item &&
    item.type === MessagePartType.ToolResponse &&
    'tool_call_id' in item &&
    'content' in item
  );
}
